import React, { memo, useMemo } from 'react';
import ModalComponent from 'v2/common/components/ModalComponent';
import withErrorBoundary from 'common/components/withErrorBoundary/index';
import { INTENT_TYPES, POINTER_MAP_TO_TITLE } from 'v2/common/constants/index';
import { extractIndividualSchemaField } from 'v2/modules/withRunForm/utils/signal';
import { TypeheadSchemaUtility } from 'v2/common/quantConfig/outputSchema/utility';
import useOutputSchema from 'v2/modules/withRunForm/hooks/useOutputSchema';
import { BaseReference, DeleteReferenceModalProps, REFERENCE_LIST } from '../../types/reference.type';
import ReactHtmlParser from 'react-html-parser';
import _ from 'lodash';

const DeleteReferenceModal: React.FC<DeleteReferenceModalProps> = ({ onClose, entity, references }) => {
  const outputSchema = useOutputSchema();
  const oneIndentation = ' ';

  const { type: entityType, index } = entity;

  const operandPairLabel = _.reduce(
    outputSchema,
    (acc: Record<string, string>, schema) => {
      acc[schema.name] = TypeheadSchemaUtility.getOption(schema)?.label || schema.label;
      return acc;
    },
    {} as Record<string, string>
  );

  const renderLabelWithBadge = (badgeText: string, labelText: number | string) => {
    return (
      <span className="leg-label-ribbon">
        <div className="badge">
          {' '}
          {badgeText} {labelText}{' '}
        </div>
      </span>
    );
  };

  const generateDependencyMessage = (reference: BaseReference) => {
    const { operand = '', pointer } = reference;
    // INTENT_TYPES.OpenBasket
    const { type } = pointer;
    const caseIndex = 'caseIndex' in pointer ? pointer.caseIndex : '';
    const intentLabel = 'intentLabel' in pointer ? pointer.intentLabel : '';
    const intentType = 'intentType' in pointer ? pointer.intentType : '';
    const pipeName = 'pipeName' in pointer ? pointer.pipeName : '';
    const adjustmentIndex = 'adjustmentIndex' in pointer ? pointer.adjustmentIndex : '';

    const { caseIndex: extractedCaseIndex, intentId } = extractIndividualSchemaField(operand);

    const getBaseMessageMap = () => {
      switch (entityType) {
        case REFERENCE_LIST.case:
          return (
            <>
              The Case {Number(extractedCaseIndex) + 1} property {renderLabelWithBadge('Leg', intentId)}
            </>
          );
        case REFERENCE_LIST.pipe:
          return (
            <>
              The{' '}
              <span className="font-weight-bold tx-13 signals-type">{_.startCase(entity.details.type)}</span>{' '}
              Indicator
            </>
          );
        case REFERENCE_LIST.intent:
          return <>The {renderLabelWithBadge('Leg', _.isNumber(index) ? Number(index) : index)}</>;
        case REFERENCE_LIST.adjustment:
          return (
            <>
              The {renderLabelWithBadge('Adjustment', Number(index) + 1)} property{' '}
              {renderLabelWithBadge('Leg', intentId)}
            </>
          );
        default:
          return null;
      }
    };

    const dependencyDetails = [
      _.isNumber(caseIndex) && `Case ${caseIndex + 1}`,
      _.isNumber(adjustmentIndex) && renderLabelWithBadge('Adjustment', adjustmentIndex + 1),
      intentLabel && (
        <>in {renderLabelWithBadge(INTENT_TYPES.OpenBasket == intentType ? 'Basket' : 'Leg', intentLabel)}</>
      ),
      type && <span className="font-weight-bold tx-13 signals-type">{POINTER_MAP_TO_TITLE[type]}</span>,
      intentType && `leg type: ${intentType}`,
      pipeName && (
        <>
          Indicator_Name:
          <span className="font-weight-bold tx-13 signals-type"> {pipeName} </span>
        </>
      ),
      operand && (
        <>
          Signal Value:
          <span className="tx-12 operand-containder">
            {oneIndentation}
            <span className="operand-info">
              {ReactHtmlParser(_.get(operandPairLabel, operand, _.startCase(String(operand))))}
            </span>
          </span>
        </>
      ),
    ].filter(Boolean);

    return (
      <>
        {getBaseMessageMap()} is used in the {oneIndentation}
        {dependencyDetails.map((item, idx) => (
          <React.Fragment key={idx}>{item} </React.Fragment>
        ))}
      </>
    );
  };

  const referenceTextMap = useMemo(() => {
    switch (entityType) {
      case REFERENCE_LIST.case:
        return `Case ${Number(index) + 1}`;
      case REFERENCE_LIST.pipe:
        return (
          <span className="font-weight-bold tx-13 signals-type">
            {_.startCase(entity.details.type)} Indicator
          </span>
        );
      case REFERENCE_LIST.intent:
        return (
          <span className="font-weight-bold tx-13 signals-type">{renderLabelWithBadge('Leg', index)}</span>
        );
      case REFERENCE_LIST.adjustment:
        return `Adjustment ${Number(index) + 1}`;
      default:
        return '';
    }
  }, [entityType, index, entity.details]);

  if (_.isEmpty(entity) || _.isEmpty(references)) return null;

  return (
    <ModalComponent
      title="Delete Dependency"
      size="lg"
      cancelBtnTitle="Close"
      btnClassName="track"
      onCancel={onClose}
      onClose={onClose}
      className="delete-info-modal"
    >
      <div className="delete-dependency-info">
        <div className="delete-dependency-head">
          <h4>
            You cannot delete
            {oneIndentation}
            {referenceTextMap}
            {oneIndentation}
            until it is removed from the following dependencies.
          </h4>
        </div>
        <div className="delete-dependency-info-list">
          <ol>
            {references.map((reference, idx) => (
              <li className="border-bottom" key={idx}>
                {generateDependencyMessage(reference)}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </ModalComponent>
  );
};

export default memo(withErrorBoundary(DeleteReferenceModal));
